import { useId } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  DEFAULT_TOOL_SETTINGS,
  INTERRUPTION_MODES,
  type InterruptionMode,
  PRE_TOOL_SPEECH_OPTIONS,
  type PreToolSpeech,
} from '@/shared/types/toolSettings';

type OptionKeys = { label: string; description: string };

const INTERRUPTION_MODE_KEYS: Record<InterruptionMode, OptionKeys> = {
  allow: {
    label: 'voice_tool_settings.interruption_mode_allow',
    description: 'voice_tool_settings.interruption_mode_allow_description',
  },
  disable_during_tool: {
    label: 'voice_tool_settings.interruption_mode_disable_during_tool',
    description: 'voice_tool_settings.interruption_mode_disable_during_tool_description',
  },
  disable_during_tool_and_turn: {
    label: 'voice_tool_settings.interruption_mode_disable_during_tool_and_turn',
    description: 'voice_tool_settings.interruption_mode_disable_during_tool_and_turn_description',
  },
};

const PRE_TOOL_SPEECH_KEYS: Record<PreToolSpeech, OptionKeys> = {
  auto: {
    label: 'voice_tool_settings.pre_tool_speech_auto',
    description: 'voice_tool_settings.pre_tool_speech_auto_description',
  },
  force: {
    label: 'voice_tool_settings.pre_tool_speech_force',
    description: 'voice_tool_settings.pre_tool_speech_force_description',
  },
  off: {
    label: 'voice_tool_settings.pre_tool_speech_off',
    description: 'voice_tool_settings.pre_tool_speech_off_description',
  },
};

type ToolSettingSelectProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

type DescribedSelectProps<T extends string> = ToolSettingSelectProps<T> & {
  options: readonly T[];
  keys: Record<T, OptionKeys>;
  defaultValue: T;
  suggestedValue?: T;
};

const Tag = ({ children, tone }: { children: string; tone: 'muted' | 'info' }) => (
  <span
    className={cn(
      'rounded border px-1.5 text-xs leading-5 font-normal',
      tone === 'info'
        ? 'border-transparent bg-info-soft text-primary'
        : 'border-border text-muted-foreground'
    )}
  >
    {children}
  </span>
);

/**
 * A select whose options each show their effect. The line under the field repeats the effect of
 * the chosen option, so the choice reads without opening the list.
 */
const DescribedSelect = <T extends string>({
  value,
  onChange,
  disabled,
  className,
  options,
  keys,
  defaultValue,
  suggestedValue,
  'aria-describedby': describedBy,
  ...triggerProps
}: DescribedSelectProps<T>) => {
  const { t } = useTranslation();
  const descriptionId = useId();

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Select
        value={value}
        onValueChange={(next) => {
          // Inside a form, Radix's hidden native select reports '' when a reset changes the value.
          if (options.includes(next as T)) {
            onChange(next as T);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className="bg-background"
          {...triggerProps}
          aria-describedby={[describedBy, descriptionId].filter(Boolean).join(' ')}
        >
          {/* Explicit children, because each item's text also holds its tags and effect. */}
          <SelectValue>{t(keys[value].label)}</SelectValue>
        </SelectTrigger>
        <SelectContent className="max-w-(--radix-select-trigger-width)">
          {options.map((option) => (
            <SelectItem key={option} value={option} indicatorPosition="left" className="py-2">
              <span className="flex min-w-0 flex-col gap-0.5 whitespace-normal">
                <span className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                  {t(keys[option].label)}
                  {option === suggestedValue && (
                    <Tag tone="info">{t('voice_tool_settings.suggested_read_only')}</Tag>
                  )}
                  {option === defaultValue && <Tag tone="muted">{t('common.default')}</Tag>}
                </span>
                <span className="text-xs leading-4 text-muted-foreground">
                  {t(keys[option].description)}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={descriptionId} className="text-xs leading-4 text-muted-foreground">
        {t(keys[value].description)}
      </p>
    </div>
  );
};

type InterruptionModeSelectProps = ToolSettingSelectProps<InterruptionMode> & {
  /** The MCP server marks the tool as read-only, so "Any time" is the suggested choice. */
  isReadOnlyTool?: boolean;
};

export const InterruptionModeSelect = ({
  isReadOnlyTool = false,
  ...props
}: InterruptionModeSelectProps) => (
  <DescribedSelect
    {...props}
    options={INTERRUPTION_MODES}
    keys={INTERRUPTION_MODE_KEYS}
    defaultValue={DEFAULT_TOOL_SETTINGS.interruption_mode}
    suggestedValue={isReadOnlyTool ? 'allow' : undefined}
  />
);

export const PreToolSpeechSelect = (props: ToolSettingSelectProps<PreToolSpeech>) => (
  <DescribedSelect
    {...props}
    options={PRE_TOOL_SPEECH_OPTIONS}
    keys={PRE_TOOL_SPEECH_KEYS}
    defaultValue={DEFAULT_TOOL_SETTINGS.pre_tool_speech}
  />
);
